import { Client } from "./_clientService";

export interface Badge {
  key: string;
  // True for badges shown to players in-game; false for stored-but-inactive badges.
  active: boolean;
  // True only for archived badges. Excluded from getBadges() unless includeArchived is passed.
  archived: boolean;
  url: string;
}

export interface BulkBadgeUploadResult {
  createdSync: number;
  queuedAsync: boolean;
  errors: { fileName: string; message: string }[];
}

export async function getBadges(includeArchived = false) {
  const path = includeArchived ? "/badges?includeArchived=true" : "/badges";
  const resp = await new Client({ path }).get();
  return resp;
}

// Mint N new badge slots. Returns [{ key, imagePath, uploadImageUrl }] — used
// by the single-file "create" flow (count: 1) via bulkUploadBadges instead;
// exposed separately in case a mint-only flow is ever needed without upload.
export async function createBadges(count: number) {
  const resp = await new Client({
    path: "/badges",
    payload: { count },
  }).post();
  return resp;
}

// Returns { key, imagePath, uploadImageUrl } — the presigned PUT URL used to
// overwrite an existing badge's webp on S3 (the same key getTeamBadgeUrl reads).
export async function getBadgeUploadUrl(key: string) {
  const resp = await new Client({
    path: "/badges/upload-url",
    payload: { key },
  }).post();
  return resp;
}

export async function setBadgeActive(key: string, isActive: boolean) {
  const resp = await new Client({
    path: `/badges/${key}/active`,
    payload: { isActive },
  }).patch();
  return resp;
}

export async function archiveBadge(key: string) {
  const resp = await new Client({ path: `/badges/${key}/archive` }).post();
  return resp;
}

export async function unarchiveBadge(key: string) {
  const resp = await new Client({ path: `/badges/${key}/unarchive` }).post();
  return resp;
}

// Backend returns CommonResultDTO { message, data: { success } } even when
// blocked (badge still in use): that's a normal response, not a thrown
// error, so callers must check `.data.data.success`, not just catch.
export async function deleteBadge(key: string) {
  const resp = await new Client({ path: `/badges/${key}` }).delete();
  return resp;
}

// Bulk create: mints N new badges and uploads art for each in one request.
export async function bulkUploadBadges(files: File[]): Promise<{ data: BulkBadgeUploadResult }> {
  const formData = new FormData();
  files.forEach((file) => formData.append("images", file));

  const resp = await new Client({
    path: "/badges/bulk-upload",
    payload: formData,
  }).post();
  return resp;
}

// Bulk replace: re-uploads art for existing badges, paired with `files` by
// array position (keys[0] gets files[0], etc — both arrays must be equal length).
export async function bulkReplaceBadges(
  keys: string[],
  files: File[],
): Promise<{ data: BulkBadgeUploadResult }> {
  const formData = new FormData();
  formData.append("keys", JSON.stringify(keys));
  files.forEach((file) => formData.append("images", file));

  const resp = await new Client({
    path: "/badges/bulk-replace",
    payload: formData,
  }).post();
  return resp;
}
